// Gemensam avslutning för alla Word-exporter:
// 1) Revisionshistorik  2) Granskning och godkännande  3) Underskrifter
// Här hämtas även sann status (Utkast/Under granskning/Godkänd) och version
// så att försättsbladet visar verkliga uppgifter i alla dokument.
import type { Paragraph, Table } from "docx";
import { supabase } from "@/integrations/supabase/client";
import { fmtDateTime, h1, body, table, type DocReviewInfo } from "@/lib/documentGenerator/docTheme";
import { REVISION_HEADERS, currentPhaseName } from "@/lib/documentGenerator/revisionColumns";
import { resolveProjectIdFromUrl } from "@/lib/documentGenerator/exportHistory";

export const REVIEW_ACTION_LABELS: Record<string, string> = {
  godkand: "Godkänt",
  komplettering: "Komplettering begärd",
  kommentar: "Kommentar",
};

/** Standardroller för underskriftsblocket per dokumentnummer. */
export const SIGNATURE_ROLES: Record<string, string[]> = {
  "01": ["Ansvarig för SAM-rutinen", "Byggherre"],
  "03": ["Byggherre", "Bas-P"],
  "04": ["Byggherre", "Bas-P"],
  "05": ["Byggherre", "Bas-P"],
  "06": ["Bas-P", "Projektör"],
  "07": ["Bas-P", "Byggherre"],
  "10": ["Bas-P", "Projektör"],
  "11": ["Byggherre", "Bas-P"],
  "12": ["Byggherre", "Bas-P", "Bas-U"],
  "15": ["Bas-U", "Entreprenör"],
  "17": ["Byggherre", "Bas-P", "Upphandlare"],
  "20": ["Bas-P", "Bas-U"],
  "22": ["Bas-U", "Byggherre"],
  "23": ["Bas-U", "Deltagare"],
  "24": ["Arbetsledare", "Bas-U", "Utförande personal"],
  "24b": ["Bas-U", "Entreprenör"],
  "25": ["Bas-P", "Bas-U"],
  "26": ["Bas-U", "Entreprenör"],
  "27": ["Bas-U", "Skyddsombud"],
  "28": ["Rapportör", "Bas-U", "Byggherre"],
  "30": ["Bas-P", "Byggherre"],
  "30b": ["Bas-P", "Byggherre"],
  "31": ["Bas-P", "Byggherre"],
  "32": ["Bas-U", "Byggherre", "Förvaltare"],
  "33": ["Byggherre", "Förvaltare"],
  "34": ["Bas-P", "Byggherre", "Förvaltare"],
};

export interface DocumentFooterOptions {
  /** Dokumentnummer enligt Bas-processen, t.ex. "12". */
  docNumber?: string | null;
  /** Dokumenttyp i exporthistoriken, t.ex. "amp" – används för versionsnummer. */
  documentType?: string | null;
  /** Projekt-id – hämtas annars ur adressen. */
  projectId?: string | null;
  /** Färdiga revisionsrader (datum, ändrad av, skede, kommentar/status). */
  revisions?: string[][];
  /** Sätt false när dokumentet redan har en egen revisionshistorik. */
  includeRevisions?: boolean;
  /** Roller i underskriftsblocket – annars standard för dokumentnumret. */
  signatureRoles?: string[];
  /** Sätt false för listor/rapporter som inte ska undertecknas. */
  includeSignatures?: boolean;
}

export interface DocumentFooterResult {
  children: (Paragraph | Table)[];
  status: string;
  version: number;
  review: DocReviewInfo;
}

const reviewRows = (
  rows: Array<{
    reviewer_name: string | null;
    reviewer_role: string | null;
    created_at: string;
    action: string;
    comment: string | null;
  }>
): string[][] =>
  rows.map((r) => [
    r.reviewer_name || "Okänd",
    r.reviewer_role || "–",
    fmtDateTime(r.created_at),
    REVIEW_ACTION_LABELS[r.action] || r.action,
    r.comment || "–",
  ]);

/**
 * Hämtar granskningsbeslut och exporthistorik för dokumentet och bygger
 * den gemensamma avslutningen.
 */
export const documentFooter = async (
  opts: DocumentFooterOptions = {}
): Promise<DocumentFooterResult> => {
  const docNumber = opts.docNumber ?? null;
  const projectId = opts.projectId ?? resolveProjectIdFromUrl();

  let reviews: DocumentFooterResult["review"]["rows"] = [];
  let approved = false;
  let status = "Utkast";
  let version = 1;

  if (projectId && docNumber) {
    try {
      const [reviewRes, exportRes] = await Promise.all([
        supabase
          .from("document_reviews")
          .select("reviewer_name, reviewer_role, created_at, action, comment")
          .eq("project_id", projectId)
          .eq("doc_number", docNumber)
          .order("created_at", { ascending: true }),
        opts.documentType
          ? supabase
              .from("document_exports")
              .select("version")
              .eq("project_id", projectId)
              .eq("document_type", opts.documentType)
              .order("version", { ascending: false })
              .limit(1)
              .maybeSingle()
          : Promise.resolve({ data: null }),
      ]);

      const list = reviewRes.data ?? [];
      reviews = reviewRows(list);
      const latest = list[list.length - 1];
      if (latest) {
        approved = latest.action === "godkand";
        status =
          latest.action === "godkand"
            ? "Godkänd"
            : latest.action === "komplettering"
              ? "Kompletteras"
              : "Under granskning";
      }
      version = ((exportRes.data as { version?: number } | null)?.version ?? 0) + 1;
    } catch {
      /* exporten ska aldrig falla på att statusen inte kunde hämtas */
    }
  }

  const children: (Paragraph | Table)[] = [];

  if (opts.includeRevisions !== false) {
    const rows =
      opts.revisions && opts.revisions.length
        ? opts.revisions
        : [[fmtDateTime(new Date().toISOString()), "–", currentPhaseName(), `Export version ${version}`]];
    children.push(h1("Revisionshistorik"));
    children.push(
      body(
        "Loggen visar vem som ändrat, i vilket skede ändringen gjordes samt kommentar/status för ändringen.",
        { muted: true }
      )
    );
    children.push(table([...REVISION_HEADERS], rows, { columnWidths: [1700, 2100, 1800, 3760] }));
  }

  children.push(h1("Granskning och godkännande"));
  children.push(
    body(
      approved
        ? "Dokumentet är godkänt av granskare och kan ingå i överlämningen."
        : "Dokumentet är inte godkänt av granskare och räknas som utkast fram till sign-off.",
      { muted: true }
    )
  );
  children.push(
    table(["Granskare", "Roll", "Datum", "Beslut", "Kommentar"], reviews, {
      columnWidths: [2000, 1500, 1700, 1700, 2460],
    })
  );

  if (opts.includeSignatures !== false) {
    const roles =
      opts.signatureRoles ?? (docNumber ? SIGNATURE_ROLES[docNumber] : undefined) ?? ["Ansvarig", "Mottagare"];
    children.push(h1("Underskrifter"));
    children.push(
      table(
        ["Roll", "Namn", "Datum", "Underskrift"],
        roles.map((role) => [role, "", "", ""]),
        { columnWidths: [2400, 2600, 1600, 2760], zebra: false }
      )
    );
  }

  return {
    children,
    status,
    version,
    review: { status, approved, rows: reviews },
  };
};
